import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import Login from './views/Login'
import Tickets from './views/Tickets'
import Categories from './views/Categories'
import Status from './views/Status'
import Groups from './views/Groups'
import Metrics from './views/Metrics'
import MyMetrics from './views/MyMetrics'
import Automations from './views/Automations'
import AiAgents from './views/AiAgents'
import AllChats from './views/AllChats'
import QuickReplies from './views/QuickReplies'
import Settings from './views/Settings'
import InternalTickets from './views/InternalTickets'
import MyInternalTickets from './views/InternalTickets/MyInternalTickets'
import TicketCategoriesManagement from './views/InternalTickets/CategoriesManagement'
import DefaultLayout from './layouts/DefaultLayout'

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { canManageSystem, isLoading } = useAuth()

  if (isLoading) {
    return null
  }

  if (!canManageSystem) {
    return <Navigate to="/tickets" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  const { isAuthenticated } = useAuth()

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/tickets" replace /> : <Login />}
      />
      <Route
        path="/"
        element={
          <PrivateRoute>
            <SocketProvider>
              <DefaultLayout />
            </SocketProvider>
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/tickets" replace />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/:id" element={<Tickets />} />
        <Route path="my-internal-tickets" element={<MyInternalTickets />} />
        <Route path="my-internal-tickets/:id" element={<MyInternalTickets />} />
        <Route
          path="internal-tickets"
          element={
            <AdminRoute>
              <InternalTickets />
            </AdminRoute>
          }
        />
        <Route
          path="internal-tickets/:id"
          element={
            <AdminRoute>
              <InternalTickets />
            </AdminRoute>
          }
        />
        <Route path="internal-ticket-categories" element={<TicketCategoriesManagement />} />
        <Route path="my-metrics" element={<MyMetrics />} />
        <Route path="quick-replies" element={<QuickReplies />} />
        <Route
          path="categories"
          element={
            <AdminRoute>
              <Categories />
            </AdminRoute>
          }
        />
        <Route
          path="status"
          element={
            <AdminRoute>
              <Status />
            </AdminRoute>
          }
        />
        <Route
          path="groups"
          element={
            <AdminRoute>
              <Groups />
            </AdminRoute>
          }
        />
        <Route
          path="metrics"
          element={
            <AdminRoute>
              <Metrics />
            </AdminRoute>
          }
        />
        <Route
          path="automations"
          element={
            <AdminRoute>
              <Automations />
            </AdminRoute>
          }
        />
        <Route
          path="ai-agents"
          element={
            <AdminRoute>
              <AiAgents />
            </AdminRoute>
          }
        />
        <Route
          path="all-chats"
          element={
            <AdminRoute>
              <AllChats />
            </AdminRoute>
          }
        />
        <Route
          path="all-chats/:id"
          element={
            <AdminRoute>
              <AllChats />
            </AdminRoute>
          }
        />
        <Route
          path="settings"
          element={
            <AdminRoute>
              <Settings />
            </AdminRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/tickets" replace />} />
    </Routes>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}

export default App
